import { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import api from "../../../api/axiosInstance";
import LogoWithHomeLink from "../../../components/LogoWithHomeLink";
import SitePageLeftHeading from "../../../components/SitePageLeftHeading";
import SiteRightContent from "../../../components/SiteRightContent";
import toast from "react-hot-toast";

export default function OtpVerification() {
  const { state } = useLocation();
  const data = state?.data;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [waitSeconds, setWaitSeconds] = useState(isNaN(data.otpResendSeconds) ? 0 : parseInt(data.otpResendSeconds))
  const [reSendingOtp, setReSendingOtp] = useState(false)

  const validationSchema = Yup.object({
    otp1: Yup.number(t("validation.number"))
      .integer()
      .min(0)
      .required(t("validation.required")),
    otp2: Yup.number(t("validation.number"))
      .integer()
      .min(0)
      .required(t("validation.required")),
    otp3: Yup.number(t("validation.number"))
      .integer()
      .min(0)
      .required(t("validation.required")),
    otp4: Yup.number(t("validation.number"))
      .integer()
      .min(0)
      .required(t("validation.required")),
    otp5: Yup.number(t("validation.number"))
      .integer()
      .min(0)
      .required(t("validation.required")),
    otp6: Yup.number(t("validation.number"))
      .integer()
      .min(0)
      .required(t("validation.required")),
  });

  const initialValues = {
    otp1: "",
    otp2: "",
    otp3: "",
    otp4: "",
    otp5: "",
    otp6: "",
  };

  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  const handleKeyUp = (event, index) => {
    if (
      event.target.value.length === 1 &&
      index < inputRefs.length - 1 &&
      !isNaN(event.target.value)
    ) {
      inputRefs[index + 1].current.focus();
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const reqData = {
        sessionId: data.sessionId,
        otp:
          values.otp1.toString() +
          values.otp2.toString() +
          values.otp3.toString() +
          values.otp4.toString() +
          values.otp5.toString() +
          values.otp6.toString(),
      };

      const response = await api.post("/api/v1/registration/verify-otp", reqData);

      navigate("/registration/verification-documents", {
        state: { data: response.data },
      });
    } catch (error) {
      const resp = error.response?.data;

      if (resp?.message) {
        toast.error(resp.message);
      }

      resetForm()
      inputRefs[0].current.focus();
    }

    setSubmitting(false);
  };

  const handleResendSubmit = async () => {
    setReSendingOtp(true);
    try{
      const response = await api.post("/api/v1/registration/resend-otp", {sessionId: data.sessionId});

      setWaitSeconds(parseInt(response.data.otpResendSeconds))
      waitCountDown()
    } catch (error) {
      const resp = error.response?.data;

      if (resp?.message) {
        toast.error(resp.message);
      }
    }

    setReSendingOtp(false);
  }

  const waitCountDown = () => {
    let interval = null

      interval = setInterval(() => {
        if (waitSeconds === 0) {
          clearInterval(interval);
        }
        else {
          setWaitSeconds((waitSeconds) => waitSeconds - 1);
        }
        
      }, 1000);

      // setWaitCountInterval(interval)
    // } else if (waitSeconds === 0) {
    //   clearInterval(interval);
    // }

    // return () => clearInterval(interval);
  }

  useEffect(() => {
    let interval = null

    if (waitSeconds > 0) {
      interval = setInterval(() => {
        setWaitSeconds((waitSeconds) => waitSeconds - 1);
      }, 1000);
    } else if (waitSeconds === 0) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [waitSeconds]);

  return (
    <>
      <Helmet>
        <title>{t("pages.registration.otp_verification.title")}</title>
      </Helmet>
      <div className="flex flex-col flex-1 w-full lg:w-1/2">
        <LogoWithHomeLink />

        <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
          <SitePageLeftHeading
            title={t("pages.registration.otp_verification.page_title")}
            description={t("pages.registration.otp_verification.description")}
          />

          <div className="space-y-5">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {(props) => (
                <Form>
                  <div className="flex flex-row">
                    <div className="flex-1/6">
                      <Field
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="otp1"
                        id="otp1"
                        maxLength={1}
                        ref={inputRefs[0]}
                        onKeyUp={(e) => handleKeyUp(e, 0)}
                        autoFocus
                        className={
                          (props.errors.otp1
                            ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                            : "border-gray-300 ") +
                          "text-center dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                        }
                      />
                    </div>

                    <div className="flex-1/6 ml-2">
                      <Field
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="otp2"
                        id="otp2"
                        maxLength={1}
                        ref={inputRefs[1]}
                        onKeyUp={(e) => handleKeyUp(e, 1)}
                        className={
                          (props.errors.otp2
                            ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                            : "border-gray-300 ") +
                          "text-center dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                        }
                      />
                    </div>

                    <div className="flex-1/6 ml-2">
                      <Field
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="otp3"
                        id="otp3"
                        maxLength={1}
                        ref={inputRefs[2]}
                        onKeyUp={(e) => handleKeyUp(e, 2)}
                        className={
                          (props.errors.otp3
                            ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                            : "border-gray-300 ") +
                          "text-center dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                        }
                      />
                    </div>

                    <div className="flex-1/6 ml-2">
                      <Field
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="otp4"
                        id="otp4"
                        maxLength={1}
                        ref={inputRefs[3]}
                        onKeyUp={(e) => handleKeyUp(e, 3)}
                        className={
                          (props.errors.otp4
                            ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                            : "border-gray-300 ") +
                          "text-center dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                        }
                      />
                    </div>

                    <div className="flex-1/6 ml-2">
                      <Field
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="otp5"
                        id="otp5"
                        maxLength={1}
                        ref={inputRefs[4]}
                        onKeyUp={(e) => handleKeyUp(e, 4)}
                        className={
                          (props.errors.otp5
                            ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                            : "border-gray-300 ") +
                          "text-center dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                        }
                      />
                    </div>

                    <div className="flex-1/6 ml-2">
                      <Field
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="otp6"
                        id="otp6"
                        maxLength={1}
                        ref={inputRefs[5]}
                        onKeyUp={(e) => handleKeyUp(e, 5)}
                        className={
                          (props.errors.otp6
                            ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                            : "border-gray-300 ") +
                          "text-center dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                        }
                      />
                    </div>
                  </div>

                  <div className="my-6">
                    <button
                      type="submit"
                      disabled={props.isSubmitting}
                      className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                    >
                      {props.isSubmitting
                        ? t("buttons.submitting")
                        : t("buttons.submit")}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>

            <div className="w-full text-sm text-center text-gray-500 dark:text-gray-400">
              {(waitSeconds > 0) && (
              <p>{waitSeconds} {t('pages.registration.otp_verification.resend_otp.info')}</p>
              )}

              {(waitSeconds == 0) && (
                <button type="button" onClick={handleResendSubmit} disabled={reSendingOtp} className="underline cursor-pointer hover:text-gray-600 dark:hover:text-white">{reSendingOtp ? t('pages.registration.otp_verification.resend_otp.button_resending') : t('pages.registration.otp_verification.resend_otp.button')}</button>
              )}
            </div>
          </div>
        </div>
      </div>

      <SiteRightContent />
    </>
  );
}
